import React, { useEffect, useRef, useState } from "react";
import {
  describeDropshippingRequest,
  isSensitiveDropshippingRequest,
  isMarkedDropshippingProductEdit,
  isMarkedDropshippingDelete,
  isMarkedDropshippingFilterUpdate,
  confirmationActionForRequest,
  withPasswordConfirmation,
} from "./dropshippingModificationGuard";
import { requestElakayPasswordConfirmation } from "./sync/passwordConfirmation";
import { clearCJConfirmation, getActiveCJConfirmationToken, isCJConfirmationError, markCJProtectedActivity } from "./sync/CJPasswordConfirmationSession";
import { elakay_API } from "../../config";
import { isAuthenticated } from "../auth";
import "./DropshippingPasswordConfirmationModal.css";
import { elakayAutoText } from "./dataElakay";

const { CJSyncActionModal } = require("./sync/CJSyncActionModal");

const requestId = () => window.crypto?.randomUUID?.() || `ds-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function DropshippingPasswordConfirmationModal() {
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const activeRef = useRef(null);

  useEffect(() => {
    const nativeFetch = window.fetch.bind(window);
    window.fetch = async (input, init = {}) => {
      if (!isSensitiveDropshippingRequest(input, init) && !isMarkedDropshippingProductEdit(input, init) && !isMarkedDropshippingDelete(input, init) && !isMarkedDropshippingFilterUpdate(input, init)) return nativeFetch(input, init);
      const activeToken = getActiveCJConfirmationToken();
      if (activeToken) {
        const response = await nativeFetch(input, withPasswordConfirmation(init, activeToken, confirmationActionForRequest(input, init), describeDropshippingRequest(input, init)));
        if (response.ok) { markCJProtectedActivity(response.headers.get("X-Elakay-CJ-Expires-At")); return response; }
        const failure = await response.clone().json().catch(() => ({}));
        if (!isCJConfirmationError(failure.code)) return response;
        clearCJConfirmation();
      }
      if (activeRef.current) return Promise.reject(new Error("Une confirmation dropshipping est déjà en cours."));
      return new Promise((resolve, reject) => {
        const details = describeDropshippingRequest(input, init);
        const operation = {
          input, init, nativeFetch, resolve, reject,
          action: confirmationActionForRequest(input, init),
          details: { ...details, requestId: requestId(), synchronizationId: `${details.provider}:${details.productId || "global"}:${Date.now()}` },
        };
        activeRef.current = operation;
        setError(null);
        setPending(operation);
      });
    };
    return () => {
      window.fetch = nativeFetch;
      if (activeRef.current) activeRef.current.reject(new DOMException("Navigation annulée", "AbortError"));
      activeRef.current = null;
    };
  }, []);

  const cancel = () => {
    if (!pending || busy) return;
    pending.reject(new DOMException("Modification annulée par l’utilisateur", "AbortError"));
    activeRef.current = null;
    setPending(null);
    setError(null);
  };

  const confirmPassword = ({ password, action }) => requestElakayPasswordConfirmation({
    baseUrl: elakay_API, token: isAuthenticated()?.token, password, action,
  });

  const execute = async ({ confirmationToken }) => {
    if (!pending || busy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await pending.nativeFetch(pending.input, withPasswordConfirmation(pending.init, confirmationToken, pending.action, pending.details));
      if (!response.ok) {
        const data = await response.clone().json().catch(() => ({}));
        throw Object.assign(new Error(data.message || data.error || "La modification dropshipping a échoué."), { code: data.code, status: response.status });
      }
      markCJProtectedActivity(response.headers.get("X-Elakay-CJ-Expires-At"));
      pending.resolve(response);
      activeRef.current = null;
      setPending(null);
    } catch (requestError) {
      setError(requestError);
      throw requestError;
    } finally {
      setBusy(false);
    }
  };

  const details = pending?.details || {};
  return (
    <CJSyncActionModal
      show={Boolean(pending)}
      onHide={cancel}
      mode="password-confirmation"
      title={elakayAutoText("h_5472f2cd28c6af", "Confirmer la modification dropshipping")}
      subtitle={`${details.provider || "Dropshipping"} · ${details.productId || "Plusieurs produits"}`}
      loading={busy}
      error={error}
      passwordRequired
      passwordLabel="Mot de passe Elakay"
      confirmationAction={pending?.action}
      onPasswordConfirm={confirmPassword}
      onConfirm={execute}
      confirmLabel="Confirmer et continuer"
      cancelLabel="Annuler"
    >
      <div className="ds-password-summary">
        <div><span>{elakayAutoText("h_97c89a4d6630ad", "Action")}</span><strong>{details.action || elakayAutoText("h_3f2dc18e4084a7", "Synchronisation")}</strong></div>
        {details.changedFields?.length > 0 && <div><span>{elakayAutoText("h_7694354611fd06", "Champs")}</span><strong>{details.changedFields.join(", ")}</strong></div>}
      </div>
    </CJSyncActionModal>
  );
}
